import type { EstimateItem } from '../types';
import { applyFactor } from '../utils/lineItemPricing';
import { computeEstimateTotals, type EstimateMathInput, type EstimateTotals } from './estimateMath';

/**
 * services/marginSolver.ts — El dial de margen neto (PLAN-pricing-model-v2, A.4).
 *
 * UNA SOLA PREGUNTA: ¿por cuánto hay que multiplicar el precio base de todas las
 * partidas —el pase directo NO— para que el estimado gane el margen que el
 * contratista pidió SOBRE SU PROPIO TRABAJO?
 *
 * Ese multiplicador es `k`, y la invariante de toda línea es `rate = baseRate × k`.
 * `k = 1` significa «exactamente mis diales»: es el ancla honesta y la posición
 * por defecto del dial.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUÉ SE SONDEA EL MOTOR Y NO SE REESCRIBE LA FÓRMULA
 *
 * Este módulo NO sabe sumar un estimado, y es a propósito. No conoce el
 * descuento, ni el recargo por terreno, ni el cargo por viaje, ni qué parte de
 * la emergencia es costo. Todo eso vive en `computeEstimateTotals` y se consulta
 * llamándolo — tres veces, con tres factores distintos.
 *
 * La alternativa (copiar aquí la fórmula del ingreso para despejar k de un
 * plumazo) es exactamente el fallo que este plan viene a corregir: hoy once
 * sitios derivan un precio de un costo por su cuenta y no coinciden entre sí.
 * Un solver con su propia aritmética empieza coincidiendo con el motor y deja de
 * hacerlo el día que alguien toca el motor — y nadie se entera, porque el número
 * de la pantalla sigue siendo plausible. Sondeando, si el motor cambia, el
 * solver cambia con él por construcción.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUÉ FORMA CERRADA Y NO BISECCIÓN
 *
 * Con k fijo, el ingreso propio y el costo propio son AFINES en k:
 *
 *     R(k) = r·k + R0        C(k) = c·k + C0
 *
 * `r` y `c` son lo que escala (las tarifas, el recargo por terreno, la
 * emergencia y su parte de sobretiempo); `R0` y `C0` lo que no (el cargo por
 * viaje, el costo de materiales y horas, el descuento plano). El pase directo se
 * resta ENTERO del ingreso propio, sin prorratear el descuento, y por eso sigue
 * siendo un sustraendo constante — de eso depende toda esta página (ola 0-B).
 *
 * Dos sondeos determinan la recta; despejar es trivial:
 *
 *     m = (R − C)/R   ⟹   k = (C0 − (1−m)·R0) / ((1−m)·r − c)
 *
 * La bisección daría el mismo número con 40 llamadas en vez de 3, y —peor— daría
 * un número *parecido* incluso donde el modelo no vale. La forma cerrada, al
 * verificarse con un tercer sondeo, o acierta al bit o se declara incapaz.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LOS CODOS: dónde la afinidad se rompe (A.4)
 *
 *   · MÍNIMO DE SERVICIO. Por debajo de él, el cliente paga el mínimo y punto:
 *     el ingreso queda CLAVADO y el margen es plano para todo un rango de k.
 *     Mover cada tarifa de la pantalla no cambia un centavo de lo que se cobra.
 *     Devolver un k ahí sería mentir dos veces: el número no hace nada y además
 *     el usuario creería que sí.
 *   · DESCUENTO PLANO TOPADO AL SUBTOTAL. Discontinuidad de verdad: por debajo
 *     del tope el subtotal es 0, por encima crece. Una recta ajustada a caballo
 *     del salto no describe nada.
 *
 * Ninguno de los dos se modela analíticamente aquí — modelarlos sería volver a
 * copiar la lógica del motor. Se DETECTAN: el tercer sondeo verifica, y si el
 * margen que sale no es el pedido, se devuelve `feasible: false` con el motivo.
 * Nunca un número inventado.
 */

/** Por qué el solver no pudo. Código estable para que la interfaz traduzca. */
export type MarginSolveReason =
    /** El objetivo no es un porcentaje utilizable (≥ 100% exige precio infinito). */
    | 'objetivo-invalido'
    /** No hay trabajo propio que escalar: estimado vacío o solo pase directo. */
    | 'sin-trabajo-propio'
    /** El precio lo fija el mínimo de servicio; las partidas no mandan. */
    | 'minimo-de-servicio'
    /** El ingreso propio no responde a k (descuento plano que se come el subtotal). */
    | 'margen-independiente-de-k'
    /** Ni con precios infinitos se llega: el objetivo pasa del techo. */
    | 'techo-inalcanzable'
    /** El objetivo cae fuera del rango que este estimado puede recorrer. */
    | 'fuera-de-rango'
    /** Se cruzó un codo: el tercer sondeo no confirmó el resultado. */
    | 'codo';

export interface MarginSolveResult {
    /** El factor a aplicar. En un fracaso vale 1 — ver la nota de abajo. */
    k: number;
    /** El margen del TRABAJO PROPIO, en puntos porcentuales, que `k` consigue de verdad. */
    achieved: number;
    feasible: boolean;
    /** Explicación en prosa del fracaso. Diagnóstico: la interfaz debe usar `reasonCode`. */
    reason?: string;
    /** Código estable del fracaso, para que la interfaz elija su propio texto traducido. */
    reasonCode?: MarginSolveReason;
    /** El límite cuando k→∞: `1 − c/r`, en puntos porcentuales. 100% si nada del costo escala. */
    ceiling?: number;
    /** El límite cuando k→0⁺: `1 − C0/R0`, en puntos porcentuales. Ausente si no está acotado. */
    floor?: number;
}

/**
 * Tolerancia de la verificación, en PUNTOS PORCENTUALES.
 *
 * No es holgura para errores del modelo: donde el modelo vale, la forma cerrada
 * acierta a nivel de doble precisión y la diferencia sale del orden de 1e-12.
 * Este 1e-6 solo absorbe el polvo de coma flotante de restar cifras grandes.
 * Cualquier desvío mayor significa que se cruzó un codo, no que «casi».
 */
const TOLERANCIA_PUNTOS = 1e-6;

/**
 * Qué se considera cero al comparar dólares.
 *
 * Relativo a la magnitud del estimado y nunca absoluto: `r` y `R0` salen de
 * RESTAR dos sondeos, y esa resta deja residuos del orden de 1e-13 en un
 * estimado de miles de dólares. Tratarlos como distintos de cero convertiría
 * «el ingreso no depende de k» en una recta con pendiente ridícula, y el solver
 * devolvería un k astronómico en vez de admitir la meseta.
 */
const CERO_RELATIVO = 1e-9;

/**
 * El precio base de una fila: `baseRate` si lo trae, y si no su `rate` de hoy.
 *
 * El respaldo NO es cosmético. Ninguna fila anterior a este modelo tiene
 * `baseRate` (el campo nació en la ola 0-A y no hubo migración: `estimates.items`
 * es jsonb y las filas viejas llegan sin la llave). Para ellas, «lo que hay hoy
 * en pantalla» ES la línea base, y k queda medido contra eso. Sin el respaldo,
 * `baseRate` sería 0 y el primer tirón del dial pondría el estimado entero a
 * cero delante del cliente.
 */
export function baseRateOf(item: EstimateItem): number {
    const base = item.baseRate;
    if (Number.isFinite(base as number)) return base as number;
    return Number.isFinite(item.rate) ? item.rate : 0;
}

/**
 * Las mismas partidas con el factor global aplicado: `rate = baseRate × k`.
 *
 * Es la ÚNICA forma de aplicar un k, y por eso se exporta: el solver la usa para
 * sondear y la interfaz debe usarla para aplicar el resultado. Si el sondeo y la
 * aplicación fueran dos códigos distintos, el margen prometido y el cobrado se
 * separarían — la misma enfermedad que este plan trata.
 *
 * TRES DECISIONES QUE PARECEN DETALLES:
 *
 *   · Se ESTAMPA `baseRate` en la salida (el que se usó, no el escalado). Así la
 *     operación es repetible: volver a resolver sobre el resultado vuelve a
 *     partir de la misma base y da el mismo precio al bit, en vez de escalar lo
 *     ya escalado. Sin esto, arrastrar el dial dos veces multiplicaría dos veces
 *     y el precio dependería del CAMINO — el fallo que se midió (clic al 10% →
 *     13,46%, arrastre al 10% → 5,71%).
 *   · El pase directo no se toca: `applyFactor` se encarga, y la regla de quién
 *     es pase directo vive en un solo sitio.
 *   · NO se reescriben `markupPercent` ni `marginPercent`. Esos son el porcentaje
 *     que el contratista tecleó o que su política produjo; derivarlos del precio
 *     escalado fabricaría el 33,6% compuesto que nadie tecleó y que originó toda
 *     esta auditoría (A.5: «nunca enseñes un porcentaje que nadie escribió»).
 *
 * `total` sí se recalcula como `rate × cantidad`, que es la invariante A.1.
 */
export function scaleItemsByFactor(items: EstimateItem[], k: number): EstimateItem[] {
    return (items ?? []).map(item => {
        const baseRate = baseRateOf(item);
        const rate = applyFactor(baseRate, k, item.type);
        const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
        return { ...item, baseRate, rate, total: rate * quantity };
    });
}

/** Un sondeo del motor real: el par (ingreso propio, costo propio) para un k. */
interface Sondeo {
    R: number;
    C: number;
    totals: EstimateTotals;
}

function sonda(input: EstimateMathInput, k: number): Sondeo {
    const totals = computeEstimateTotals({ ...input, items: scaleItemsByFactor(input.items ?? [], k) });
    /* `costOwnWork` no es un campo de EstimateTotals: se deriva exacto restando
     * el costo del pase directo, que por definición ES su ingreso. Restar aquí y
     * no sumar por otro lado mantiene la identidad estructural del motor. */
    return { R: totals.revenueOwnWork, C: totals.totalCost - totals.passThroughCost, totals };
}

/**
 * El factor `k` que lleva el margen del TRABAJO PROPIO al objetivo pedido.
 *
 * @param input          El estimado entero, tal como se le pasa a `computeEstimateTotals`.
 * @param targetPercent  Margen objetivo en PUNTOS PORCENTUALES, sobre el PRECIO
 *                       (margen, no markup) y medido solo sobre el trabajo propio.
 *
 * EN UN FRACASO DEVUELVE `k = 1`, no 0 ni NaN. Quien ignore `feasible` y aplique
 * el factor igualmente se encuentra con una operación NEUTRA —los precios se
 * quedan donde estaban— en lugar de un documento de $0 delante del cliente.
 * `achieved` es siempre el margen que consigue el `k` devuelto, también al
 * fracasar: nunca es una promesa, siempre es una medición.
 */
export function solveScaleForNetMargin(input: EstimateMathInput, targetPercent: number): MarginSolveResult {
    const p1 = sonda(input, 1);
    /* El ancla del fracaso: k = 1 y el margen que ese k da de verdad. */
    const neutro = { k: 1, achieved: p1.totals.marginPercentOwnWork, feasible: false as const };

    if (!Number.isFinite(targetPercent) || targetPercent >= 100) {
        return {
            ...neutro,
            reasonCode: 'objetivo-invalido',
            reason: 'El margen objetivo tiene que ser un número menor que 100%: un margen del 100% '
                + 'exige un precio infinito, porque el margen se mide SOBRE EL PRECIO.',
        };
    }

    const p2 = sonda(input, 2);

    // La recta, a partir de los dos sondeos: R(k) = r·k + R0, C(k) = c·k + C0.
    const r = p2.R - p1.R;
    const c = p2.C - p1.C;
    const R0 = p1.R - r;
    const C0 = p1.C - c;

    const escala = Math.max(Math.abs(r), Math.abs(c), Math.abs(R0), Math.abs(C0), 1);
    const cero = escala * CERO_RELATIVO;

    /* El techo: cuando k→∞ el ingreso y el costo quedan dominados por lo que
     * escala, así que m → 1 − c/r. Si nada del costo escala (c = 0, el caso
     * normal: materiales y horas cuestan lo que cuestan aunque se cobre el
     * doble), el techo es el 100% y no hay objetivo razonable fuera de alcance.
     * Se devuelve siempre que exista —también en el éxito— para que la interfaz
     * pueda avisar ANTES de que el usuario arrastre hasta lo imposible. */
    const ceiling = Math.abs(r) > cero ? (1 - c / r) * 100 : undefined;
    /* El piso: cuando k→0⁺ solo queda lo que no escala, m → 1 − C0/R0. Con
     * R0 = 0 y costo fijo positivo el margen se hunde sin límite (ingreso → 0
     * con costo > 0), así que no hay piso que devolver y se omite; decir «-100%»
     * sería inventar una cota que no existe. Con R0 = 0 y C0 = 0 el margen ni se
     * mueve: piso y techo son el mismo número. */
    const floor = R0 > cero
        ? (1 - C0 / R0) * 100
        : (Math.abs(R0) <= cero && Math.abs(C0) <= cero ? ceiling : undefined);
    const limites = { ceiling, floor };

    /* ── Codo 1: el ingreso propio no se mueve con k ─────────────────────────
     * Aquí no hay recta que despejar, y da igual que el COSTO sí varíe (la parte
     * de sobretiempo del recargo de emergencia lo hace): si el cliente paga lo
     * mismo con cualquier tarifa, el dial es un adorno y hay que decirlo. */
    if (Math.abs(r) <= cero) {
        if (Math.abs(p1.R) <= cero) {
            return {
                ...neutro, ...limites,
                reasonCode: 'sin-trabajo-propio',
                reason: 'Este estimado no tiene trabajo propio que escalar: o está vacío, o todo lo '
                    + 'que contiene es pase directo (permisos y tasas, que se remiten a costo). '
                    + 'El dial de margen no tiene sobre qué actuar.',
            };
        }
        if (p1.totals.finalMinFee > 0 || p2.totals.finalMinFee > 0) {
            return {
                ...neutro, ...limites,
                reasonCode: 'minimo-de-servicio',
                reason: 'El precio lo fija el mínimo de servicio, no las partidas: por debajo de ese '
                    + 'mínimo el cliente paga el mínimo, así que mover las tarifas de la pantalla no '
                    + 'cambia ni un centavo de lo que se cobra.',
            };
        }
        return {
            ...neutro, ...limites,
            reasonCode: 'margen-independiente-de-k',
            reason: 'El ingreso de este estimado no responde al dial —lo habitual es un descuento '
                + 'plano que se come el subtotal entero—, así que el margen es el mismo para '
                + 'cualquier factor.',
        };
    }

    /* Sentido de m(k). Derivando m = 1 − C/R sale que el signo de la pendiente es
     * el de (C0·r − c·R0), CONSTANTE: el margen es monótono en k mientras el
     * ingreso no cambie de signo. Por eso `ceiling` es de verdad un techo cuando
     * esto es positivo, y un suelo cuando es negativo. */
    const sentido = C0 * r - c * R0;
    const m = targetPercent / 100;

    /* Pendiente exactamente nula: el ingreso y el costo escalan en la misma
     * proporción, así que el margen es el MISMO para cualquier k. Pasa cuando
     * nada es fijo —ni costo de material, ni horas, ni viaje— y solo escala lo
     * proporcional. Aquí no hay codo ni techo: hay una constante, y decirlo así
     * es más útil que dejar que el tercer sondeo lo rechace como «codo». */
    if (sentido === 0) {
        return {
            ...neutro, ...limites,
            reasonCode: 'margen-independiente-de-k',
            reason: `En este estimado el ingreso y el costo suben en la misma proporción, así que el `
                + `margen se queda en ${(ceiling ?? 0).toFixed(2)}% se ponga el factor que se ponga.`,
        };
    }

    if (ceiling !== undefined) {
        const inalcanzable = sentido > 0 ? targetPercent >= ceiling : targetPercent <= ceiling;
        if (inalcanzable) {
            return {
                ...neutro, ...limites,
                reasonCode: 'techo-inalcanzable',
                reason: `Ni subiendo los precios sin límite se llega: el máximo que este estimado `
                    + `puede dar sobre el trabajo propio es ${ceiling.toFixed(2)}%. Para pasar de ahí `
                    + `hay que bajar el costo, no subir la tarifa.`,
            };
        }
    }

    const denominador = (1 - m) * r - c;
    if (Math.abs(denominador) <= cero) {
        return {
            ...neutro, ...limites,
            reasonCode: 'codo',
            reason: 'El objetivo coincide con el límite al que tiende este estimado, así que no hay '
                + 'un factor finito que lo consiga.',
        };
    }

    const k = (C0 - (1 - m) * R0) / denominador;
    if (!Number.isFinite(k) || k <= 0) {
        return {
            ...neutro, ...limites,
            reasonCode: 'fuera-de-rango',
            reason: 'El objetivo cae fuera de lo que este estimado puede recorrer subiendo o bajando '
                + 'sus precios. Un factor negativo o nulo no es un precio.',
        };
    }

    /* ── El tercer sondeo: verificar, no confiar ─────────────────────────────
     * Los dos primeros sondeos SUPONEN que la afinidad vale en todo el recorrido.
     * Este comprueba que valía justo donde cayó la respuesta. Es lo único que
     * separa «resuelto» de «plausible». */
    const p3 = sonda(input, k);
    const achieved = p3.totals.marginPercentOwnWork;

    /* El mínimo de servicio pudo activarse al bajar los precios: entonces el
     * cliente pagaría el mínimo y no lo que dicen las partidas, aunque el margen
     * verificara por casualidad. Se rechaza con el motivo concreto. */
    if (p3.totals.finalMinFee > 0) {
        return {
            ...neutro, ...limites,
            reasonCode: 'minimo-de-servicio',
            reason: 'Con ese margen el trabajo cae por debajo del mínimo de servicio, y entonces el '
                + 'precio lo fija el mínimo y no las partidas.',
        };
    }

    if (Math.abs(achieved - targetPercent) > TOLERANCIA_PUNTOS) {
        return {
            ...neutro, ...limites,
            reasonCode: 'codo',
            reason: 'Se cruzó un codo del modelo —el mínimo de servicio o un descuento plano topado '
                + 'al subtotal— y el factor calculado no se pudo confirmar. No se devuelve un número '
                + 'inventado: el margen que saldría no sería el pedido.',
        };
    }

    return { k, achieved, feasible: true, ...limites };
}
